import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  X,
  Save,
  Calendar as CalendarIcon,
  Edit,
  Loader2,
  Users,
} from "lucide-react";
import { format, isSameDay, isBefore, startOfDay } from "date-fns";
import {
  useAvailableSlots,
  useAvailableDates,
  useCreateBooking,
} from "@/hooks/useBookings";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { showSuccess, showError, showWarning } from "@/utils/toastHelpers";
import { getDoctorName } from "@/utils/dataLookups";
import { formatDate } from "@/utils/dateUtils";
import { getErrorMessage } from "@/utils/errorUtils";

// Import shared components
import TimeSlotSelection from "./TimeSlotSelection";
import DoctorSelectionForPracticeOwners from "./DoctorSelectionForPracticeOwners";
import ServiceInfoDisplay from "./ServiceInfoDisplay";

export default function DoctorAppointmentForm({
  appointmentToEdit,
  serviceForBooking,
  services,
  onSave,
  onClose,
}) {
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    patient_id: "",
    patient_database_id: "",
    patient_email: "",
    service_id: "",
    practice_id: "",
    date: "",
    start_time: "",
    end_time: "",
    doctor_id: "",
    room_id: "",
    status: "scheduled",
    notes: "",
    total_price: 0,
  });

  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [doctorFilter, setDoctorFilter] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState("");

  const isEditing = !!appointmentToEdit;
  const dateStr = selectedDate ? format(selectedDate, "yyyy-MM-dd") : null;

  // Get available dates and comprehensive availability data
  const { data: availabilityData, isLoading: loadingAvailability } =
    useAvailableDates(selectedService?.id, null, 60);

  // Fetch doctors data for name lookups
  const { data: doctors = [] } = useQuery({
    queryKey: ["doctors"],
    queryFn: () => apiClient.get("/api/users/doctors"),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch patient profiles for selection
  const { data: patientProfiles = [] } = useQuery({
    queryKey: ["patientProfiles"],
    queryFn: () => apiClient.get("/api/patients"),
    staleTime: 5 * 60 * 1000,
  });

  // Convert dates to Set for faster lookup
  const availableDates = useMemo(() => {
    if (Array.isArray(availabilityData)) {
      // When no dates available, availabilityData is an empty array
      return new Set([]);
    }
    // When dates are available, availabilityData has a dates property
    return new Set(availabilityData?.dates || []);
  }, [availabilityData]);

  // Get slots from cached availability data when possible
  const slotsForSelectedDate = useMemo(() => {
    if (!selectedDate || !availabilityData?.availability) return [];

    const dateEntry = availabilityData.availability.find(
      (item) => item.date === dateStr
    );
    return dateEntry?.slots || [];
  }, [availabilityData?.availability, dateStr, selectedDate]);

  // Only make API call if we don't have cached slots
  const hasCachedSlots = slotsForSelectedDate.length > 0;
  const { data: apiSlotsData, isLoading: loadingSlots } = useAvailableSlots(
    selectedService?.id,
    hasCachedSlots ? null : dateStr
  );

  // Use cached slots when available, otherwise use API data
  const allSlots = useMemo(() => {
    return slotsForSelectedDate.length > 0
      ? slotsForSelectedDate
      : apiSlotsData || [];
  }, [slotsForSelectedDate, apiSlotsData]);

  const actuallyLoadingSlots = !hasCachedSlots && loadingSlots;

  // Filter slots based on selected doctor
  const availableSlots = useMemo(() => {
    const currentService = selectedService || serviceForBooking;

    if (!currentService) return [];
    if (currentService.requires_doctor && !doctorFilter) return [];
    if (!allSlots.length) return [];

    return allSlots.filter((slot) =>
      currentService.requires_doctor ? slot.doctorId === doctorFilter : true
    );
  }, [allSlots, doctorFilter, selectedService, serviceForBooking]);

  const createBookingMutation = useCreateBooking();

  // Initialize form data
  useEffect(() => {
    if (isEditing) {
      setFormData({
        id: appointmentToEdit.id,
        patient_id: appointmentToEdit.patient_id || "",
        patient_database_id: appointmentToEdit.patient_database_id || "",
        patient_email: appointmentToEdit.patient_email || "",
        service_id: appointmentToEdit.service_id || "",
        date: appointmentToEdit.date || "",
        start_time: appointmentToEdit.start_time || "",
        end_time: appointmentToEdit.end_time || "",
        doctor_id: appointmentToEdit.doctor_id || "",
        room_id: appointmentToEdit.room_id || "",
        status: appointmentToEdit.status || "scheduled",
        notes: appointmentToEdit.notes || "",
        total_price: appointmentToEdit.total_price || 0,
      });

      // Find the patient profile for editing
      // First try to match by database ID (patient_database_id), then by identification number
      let patient = patientProfiles.find(
        (p) => p.id === appointmentToEdit.patient_database_id
      );

      if (!patient) {
        // Fallback: try to match by identification number
        patient = patientProfiles.find(
          (p) => p.identificationNumber === appointmentToEdit.patient_id
        );
      }

      if (patient) {
        setSelectedPatient(patient.id);
      }

      if (appointmentToEdit.date) {
        setSelectedDate(new Date(appointmentToEdit.date));
      }

      if (appointmentToEdit.doctor_id) {
        setDoctorFilter(appointmentToEdit.doctor_id);
      }

      // Find and set the service for editing
      const service = services.find(
        (s) => s.id === appointmentToEdit.service_id
      );
      setSelectedService(service);
    } else if (serviceForBooking) {
      const service =
        services.find((s) => s.id === serviceForBooking.id) ||
        serviceForBooking;
      setSelectedService(service);
      setFormData((prev) => ({
        ...prev,
        service_id: serviceForBooking.id,
        practice_id:
          serviceForBooking.selectedPracticeId ||
          serviceForBooking.practice_id ||
          user?.practiceId ||
          "",
        total_price: service?.base_price || 0,
      }));
    }
  }, [
    appointmentToEdit,
    serviceForBooking,
    isEditing,
    services,
    user,
    patientProfiles,
  ]);

  const handlePatientSelect = (patientId) => {
    setSelectedPatient(patientId);
    const patient = patientProfiles.find((p) => p.id === patientId);
    if (patient) {
      setFormData((prev) => ({
        ...prev,
        patient_id: patient.identificationNumber,
        patient_database_id: patient.id,
        patient_email: patient.email,
      }));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDateSelect = (date) => {
    if (!date) {
      setSelectedDate(null);
      setFormData((prev) => ({
        ...prev,
        date: "",
        start_time: "",
        end_time: "",
        doctor_id: "",
      }));
      setDoctorFilter(null);
      return;
    }

    if (!(date instanceof Date) || isNaN(date.getTime())) {
      console.warn("Invalid date selected:", date);
      return;
    }

    setSelectedDate(date);
    const dateStr = format(date, "yyyy-MM-dd");
    setFormData((prev) => ({
      ...prev,
      date: dateStr,
      start_time: "",
      end_time: "",
      doctor_id: "",
    }));

    setDoctorFilter(null);
  };

  const handleTimeSlotSelect = (slot) => {
    setFormData((prev) => ({
      ...prev,
      start_time: slot.startTime,
      end_time: slot.endTime,
      doctor_id: doctorFilter || slot.doctorId || "",
      room_id: slot.roomId || "",
    }));
  };

  const isDateAvailable = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return availableDates.has(dateStr);
  };

  const isDateDisabled = (date) => {
    if (isBefore(date, startOfDay(new Date()))) return true;
    if (isEditing && selectedDate && isSameDay(date, selectedDate))
      return false;
    return !isDateAvailable(date);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedPatient) {
      showWarning("Please select a patient");
      return;
    }

    if (!formData.date || !formData.start_time) {
      showWarning("Please select date and time");
      return;
    }

    try {
      if (isEditing) {
        await onSave(formData);
        onClose();
        showSuccess("Appointment updated successfully");
      } else {
        await createBookingMutation.mutateAsync(formData);
        onClose();
        showSuccess("Appointment created successfully");
      }
    } catch (error) {
      console.error("Error saving appointment:", error);
      showError("Failed to save appointment", getErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-5xl bg-white/90 backdrop-blur-xl border-slate-200/60 shadow-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-white/80 backdrop-blur-sm z-10 border-b">
          <CardTitle className="flex items-center gap-2">
            {isEditing ? (
              <Edit className="w-5 h-5" />
            ) : (
              <CalendarIcon className="w-5 h-5" />
            )}
            {isEditing ? "Edit Appointment" : "Schedule Appointment"}
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div
              className={`grid gap-8 ${
                availableDates.size > 0 ? "lg:grid-cols-3" : "lg:grid-cols-2"
              }`}
            >
              {/* Left Column: Service and Patient Info */}
              <div className="space-y-6">
                <ServiceInfoDisplay selectedService={selectedService} />

                {/* Patient Selection */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-orange-800 mb-3">
                    <Users className="w-4 h-4" />
                    <span className="font-medium">
                      {isEditing ? "Patient" : "Select Patient *"}
                    </span>
                  </div>
                  {isEditing ? (
                    <div className="w-full p-3 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                      <div className="text-sm text-gray-500 mb-1">
                        Patient cannot be changed when editing
                      </div>
                      <div className="font-medium">
                        {(() => {
                          const patient = patientProfiles.find(
                            (p) => p.id === selectedPatient
                          );
                          return patient
                            ? `${patient.name} - ${patient.email}`
                            : "Loading patient...";
                        })()}
                      </div>
                    </div>
                  ) : (
                    <Select
                      value={selectedPatient}
                      onValueChange={handlePatientSelect}
                    >
                      <SelectTrigger className="w-full bg-white" data-testid="appointment-patient-select">
                        <SelectValue placeholder="Choose a patient..." />
                      </SelectTrigger>
                      <SelectContent>
                        {patientProfiles.length === 0 ? (
                          <SelectItem value="no-patients" disabled>
                            No patients found
                          </SelectItem>
                        ) : (
                          patientProfiles.map((patient, index) => (
                            <SelectItem key={patient.id} value={patient.id} data-testid={`appointment-patient-option-${index}`}>
                              {patient.name} - {patient.email}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  )}

                  {/* Selected Patient Display */}
                  {selectedPatient &&
                    (() => {
                      const patient = patientProfiles.find(
                        (p) => p.id === selectedPatient
                      );
                      return patient ? (
                        <div className="mt-3 p-3 bg-white rounded border">
                          <div className="text-sm font-medium text-gray-900">
                            {patient.name}
                          </div>
                          <div className="text-sm text-gray-600">
                            {patient.email}
                          </div>
                          <div className="text-sm text-gray-600">
                            ID: {patient.identificationNumber}
                          </div>
                          {patient.dateOfBirth && (
                            <div className="text-sm text-gray-600">
                              DOB: {formatDate(patient.dateOfBirth)}
                            </div>
                          )}
                        </div>
                      ) : null;
                    })()}
                </div>

                {/* Notes Field */}
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleInputChange("notes", e.target.value)}
                    placeholder="Any special instructions or notes..."
                    rows={3}
                    data-testid="appointment-notes-input"
                  />
                </div>
              </div>

              {/* Middle Column: Calendar */}
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Select Date *</Label>
                  {loadingAvailability ? (
                    <div className="mt-2 p-6 text-center bg-slate-50 border border-slate-200 rounded-lg">
                      <Loader2 className="w-8 h-8 mx-auto text-slate-400 mb-3 animate-spin" />
                      <p className="text-slate-600 text-sm">
                        Loading available dates...
                      </p>
                    </div>
                  ) : !loadingAvailability &&
                    availabilityData &&
                    availableDates.size === 0 ? (
                    <div className="mt-2 p-6 text-center bg-amber-50 border border-amber-200 rounded-lg">
                      <CalendarIcon className="w-12 h-12 mx-auto text-amber-400 mb-3" />
                      <h3 className="text-lg font-medium text-amber-800 mb-2">
                        No Available Dates
                      </h3>
                      <p className="text-amber-700 text-sm">
                        There are currently no available appointment slots for
                        this service.
                        {selectedService?.requires_doctor && (
                          <span className="block mt-1">
                            This may be because no doctors are available or
                            assigned to this service.
                          </span>
                        )}
                      </p>
                      <p className="text-amber-600 text-xs mt-2">
                        Please try selecting a different service or contact the
                        practice to schedule.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="mt-2 flex justify-center">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={handleDateSelect}
                          disabled={isDateDisabled}
                          className="rounded-md border"
                          modifiers={{
                            available: (date) =>
                              isDateAvailable(date) &&
                              !isBefore(date, startOfDay(new Date())),
                            unavailable: (date) =>
                              !isDateAvailable(date) &&
                              !isBefore(date, startOfDay(new Date())),
                            today: (date) => isSameDay(date, new Date()),
                            selected: (date) =>
                              selectedDate && isSameDay(date, selectedDate),
                          }}
                          modifiersStyles={{
                            available: {
                              backgroundColor: "#dbeafe",
                              color: "#1d4ed8",
                            },
                            unavailable: {
                              backgroundColor: "#f1f5f9",
                              color: "#94a3b8",
                              textDecoration: "line-through",
                            },
                            today: {
                              border: "2px solid #3b82f6",
                              fontWeight: "bold",
                            },
                            selected: {
                              backgroundColor: "#16a34a",
                              color: "white",
                              fontWeight: "bold",
                              border: "2px solid #15803d",
                              borderRadius: "4px",
                            },
                          }}
                        />
                      </div>

                      {/* Legend */}
                      <div className="flex justify-center gap-4 text-xs">
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-blue-200 rounded"></div>
                          <span>Available</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-green-500 rounded"></div>
                          <span>Selected</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-slate-200 rounded"></div>
                          <span>Unavailable</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Right Column: Doctor Selection and Time Slots */}
              {availableDates.size > 0 && (
                <div className="space-y-6">
                  <DoctorSelectionForPracticeOwners
                    selectedService={selectedService}
                    selectedDate={selectedDate}
                    allSlots={allSlots}
                    doctorFilter={doctorFilter}
                    loadingSlots={actuallyLoadingSlots}
                    practiceId={
                      serviceForBooking?.selectedPracticeId ||
                      serviceForBooking?.practice_id ||
                      user?.practiceId
                    }
                    onDoctorFilterChange={(value) => {
                      setDoctorFilter(value);
                      setFormData((prev) => ({
                        ...prev,
                        start_time: "",
                        end_time: "",
                        doctor_id: value,
                      }));
                    }}
                  />

                  <TimeSlotSelection
                    selectedDate={selectedDate}
                    selectedService={selectedService}
                    doctorFilter={doctorFilter}
                    formData={formData}
                    availableSlots={availableSlots}
                    loadingSlots={actuallyLoadingSlots}
                    onTimeSlotSelect={handleTimeSlotSelect}
                  />

                  {/* Selected Time Display */}
                  {selectedDate && formData.start_time && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="text-sm font-medium text-green-900">
                        Selected Time
                      </div>
                      <div className="text-green-700">
                        {format(selectedDate, "EEEE, MMM d, yyyy")} at{" "}
                        {format(
                          new Date(`2024-01-01T${formData.start_time}`),
                          "h:mm a"
                        )}
                        {formData.end_time && (
                          <>
                            {" "}
                            -{" "}
                            {format(
                              new Date(`2024-01-01T${formData.end_time}`),
                              "h:mm a"
                            )}
                          </>
                        )}
                      </div>
                      {formData.doctor_id && (
                        <div className="text-sm text-green-600 mt-1">
                          Doctor:{" "}
                          {allSlots.find(
                            (slot) => slot.doctorId === formData.doctor_id
                          )?.doctorName ||
                            getDoctorName(formData.doctor_id, doctors) ||
                            "Selected Doctor"}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-blue-600 to-indigo-600"
                disabled={
                  !selectedDate ||
                  !formData.start_time ||
                  !selectedPatient ||
                  createBookingMutation.isPending ||
                  (selectedService?.requires_doctor && !formData.doctor_id)
                }
                data-testid="appointment-submit-btn"
              >
                {createBookingMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {isEditing ? "Update Appointment" : "Schedule Appointment"}
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
